import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import { ReactComponent as CompanyIcon } from '../../assets/icons/system details small/System name icon.svg';
import { ReactComponent as AddressIcon } from '../../assets/icons/system details small/Address icon.svg';
import { useSiteInfo } from '../../shared/appUIFramework/appBackend/useSiteInfo';
import { AppPermissionComponentGuard } from '../../shared/appUIFramework/appPermisions/AppPermisionGuard';
import { CanEditSiteDetailsPermissions } from '../../shared/appUIFramework/appPermisions/permissionsGroups';
import AppShowLoading from '../../shared/appUIFramework/components/AppShowLoading';
import { countryList } from '../../shared/appUIFramework/constants/Countries';
import SystemTabsHeader from './components/SystemTabsHeader';
import { formatSystemAddress } from '../../shared/appUIFramework/formatters/formatSiteAddress';
import './SystemTabsSystemDetails.scss';
import './SystemTabsSystemDetailsSolo.overrides.scss';
import {
  AccountStatusUIState,
  applyFunctionalityLimitedStyles,
  useAccountInfo,
} from '../../shared/appUIFramework/appBackend/useAccountInfo';
import { useEntryOrSoloSystemRoute } from '../../shared/appShellComponents/AppRouteSwitch';

function SystemDetailsPaper({ title, disabled, children }: { title: string, disabled: boolean, children: ReactNode }) {
  return (
    <div className="app-system-details-paper" aria-disabled={disabled}>
      <div className="app-system-details-paper-title">
        {title}
      </div>
      <div className="app-system-details-paper-content">
        {children}
      </div>
    </div>
  );
}

function SystemDetailsPaperItem({ icon, content }: { icon: ReactNode, content: ReactNode }) {
  return (
    <div className="app-system-details-paper-content-item">
      <div className="app-system-details-paper-content-item-icon">
        {icon}
      </div>
      <div className="app-system-details-paper-content-item-content">
        {content}
      </div>
    </div>
  );
}

export default function SystemTabsSystemDetailsSolo() {
  const { t } = useTranslation();
  const [, params] = useEntryOrSoloSystemRoute('/systems/solo/:siteId/system-details');
  const { siteInfo } = useSiteInfo(params?.siteId || '');
  const { accountInfo } = useAccountInfo();
  const isAccountFunctionalityLimited = accountInfo.accountStatus === AccountStatusUIState.Limited;

  return (
    <>
      <SystemTabsHeader activeTab="system-details" solo />
      <div className="app-content app-pt-20 app-pb-33 app-px-60">
        <AppShowLoading showLoading={!siteInfo}>
          <div className="app-text-align-right">
            <AppPermissionComponentGuard permissions={CanEditSiteDetailsPermissions}>
              {isAccountFunctionalityLimited
                ? (
                  <span className="app-primary-button disabled-green" {...applyFunctionalityLimitedStyles(true)}>
                    {t('Solo_EditSystemDetails')}
                  </span>
                )
                : (
                  <Link
                    to={`/systems/solo/${params?.siteId}`}
                    className="app-primary-button"
                  >
                    {t('Solo_EditSystemDetails')}
                  </Link>
                )}
            </AppPermissionComponentGuard>
          </div>
          <div className="app-mb-46 app-system-details-papers app-system-details-papers-solo">
            <SystemDetailsPaper disabled={isAccountFunctionalityLimited} title={t('Solo_SystemDetails')}>
              <SystemDetailsPaperItem
                icon={<CompanyIcon />}
                content={siteInfo?.siteName}
              />
              <SystemDetailsPaperItem
                icon={<AddressIcon />}
                content={(
                  <>
                    <div>{formatSystemAddress(siteInfo?.siteAddress)}</div>
                    <div>
                      {countryList.find((p) => p.countryCode === siteInfo?.siteAddress?.countryCode)?.name}
                    </div>
                  </>
                )}
              />
            </SystemDetailsPaper>
          </div>
        </AppShowLoading>
      </div>
    </>
  );
}
